"use client";
import Link from 'next/link';
import Select from 'react-select';
import { useState, useContext } from 'react';
import { Checkbox } from 'pretty-checkbox-react';
import { ModalContext } from '@/providers/modalProvider';
import styles from './styles.module.scss';
import useDisableScroll from '@/hooks/useDisableScroll';


const experienceOptions = [
    {value: "associate", label: "Associate"},
    {value: "director", label: "Director"},
    {value: "entry_level", label: "Associate"},
    {value: "executive", label: "Executive"},
    {value: "internship", label: "Internship"},
    {value: "mid_senior_level", label: "Mid-Senior Level"},
    {value: "not_applicable", label: "Associate"}
];
const functionOptions = [
    { value: 'accounting_auditing', label: 'Accounting/Auditing' },
    { value: 'administrative', label: 'Administrative' },
    { value: 'advertising', label: 'Advertising' },
    { value: 'analyst', label: 'Analyst' },
    { value: 'art_creative', label: 'Art/Creative' },
    { value: 'business_development', label: 'Business Development' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'customer_service', label: 'Customer Service' },
    { value: 'design', label: 'Design' },
    { value: 'distribution', label: 'Distribution' },
    { value: 'education', label: 'Education' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'finance', label: 'Finance' },
    { value: 'general_business', label: 'General Business' },
    { value: 'health_care_provider', label: 'Health Care Provider' },
    { value: 'human_resources', label: 'Human Resources' },
    { value: 'information_technology', label: 'Information Technology' },
    { value: 'legal', label: 'Legal' },
    { value: 'management', label: 'Management' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'other', label: 'Other' },
    { value: 'product_management', label: 'Product Management' },
    { value: 'production', label: 'Production' },
    { value: 'project_management', label: 'Project Management' },
    { value: 'public_relations', label: 'Public Relations' },
    { value: 'purchasing', label: 'Purchasing' },
    { value: 'quality_assurance', label: 'Quality Assurance' },
    { value: 'research', label: 'Research' },
    { value: 'sales', label: 'Sales' },
    { value: 'science', label: 'Science' },
    { value: 'strategy_planning', label: 'Strategy/Planning' },
    { value: 'supply_chain', label: 'Supply Chain' },
    { value: 'training', label: 'Training' },
    { value: 'writing_editing', label: 'Writing/Editing' }
];
const typeOptions = [
    { value: 'casual', label: 'Casual' },
    { value: 'contract', label: 'Contract' },
    { value: 'permanent', label: 'Full-time' },
    { value: 'intern', label: 'Intern' },
    { value: 'part-time', label: 'Part-time' }
];

const InputField = ({ label, name, type = "text", value, onChange, required = false, error }) => (
    <fieldset className="fieldset">
        <label className="label">{label}</label>
        <input
            type={type}
            name={name}
            className={`input ${error ? 'input_error' : ''}`}
            value={value}
            onChange={onChange}
            required={required}
        />
    </fieldset>
);


const JobAlertForm = () => {
    useDisableScroll(true);
    const { closeModal } = useContext(ModalContext) || {};

    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        keywords: '',
        location: '',
        experience: '',
        function: '',
        employmentType: '',
        checkboxes: { consent: false }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSelectChange = (name, selectedOption) => {
        setFormData(prevState => ({
            ...prevState,
            [name]: selectedOption.value
        }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            checkboxes: {
                ...prevState.checkboxes,
                [name]: checked
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(false);

        try {
            const response = await fetch('/api/job_alert', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error('Failed to submit form');

            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                keywords: '',
                location: '',
                experience: '',
                function: '',
                employmentType: '',
                checkboxes: { consent: false }
            });
            setSubmitted(true);
            closeModal();
        } catch (error) {
            setError(true);
            setSubmitted(false);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseClick = (e) => {
        e.preventDefault();
        closeModal();
    };

    return (
        <div className={styles.jobAlert}>
            <h3 className={styles.jobAlert__title}>Create Job Alert</h3>
            <form className={styles.jobAlert__form} onSubmit={handleSubmit}>
                <div>
                    <InputField
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        error={error}
                    />
                    <InputField
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        error={error}
                    />
                </div>
                <div>
                    <InputField
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        error={error}
                    />
                    <InputField
                        label="Keyword"
                        name="keywords"
                        value={formData.keywords}
                        onChange={handleChange}
                        required
                        error={error}
                    />
                </div>
                <div>
                    <InputField
                        label="Location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        required
                        error={error}
                    />
                    <fieldset className="fieldset">
                        <label className="label">Level of Experience</label>
                        <Select
                            classNamePrefix="custom_select"
                            placeholder="Level of Experience"
                            options={experienceOptions}
                            onChange={selectedOption => handleSelectChange('experience', selectedOption)}
                        />
                    </fieldset>
                </div>
                <div>
                    <fieldset className="fieldset">
                        <label className="label">Function</label>
                        <Select
                            classNamePrefix="custom_select"
                            placeholder="Function"
                            options={functionOptions}
                            onChange={selectedOption => handleSelectChange('function', selectedOption)}
                        />
                    </fieldset>
                    <fieldset className="fieldset">
                        <label className="label">Type of Employment</label>
                        <Select
                            classNamePrefix="custom_select"
                            placeholder="Type of Employment"
                            options={typeOptions}
                            onChange={selectedOption => handleSelectChange('employmentType', selectedOption)}
                        />
                    </fieldset>
                </div>
                <div className={styles.jobAlert__consent}>
                    <Checkbox
                        name="consent"
                        checked={formData.checkboxes.consent}
                        onChange={handleCheckboxChange}
                    />
                    <p>
                        By checking this box, you agree to receive emails from SmartRecruiters about Daxko jobs matching your criteria, according to Daxko Privacy Policy.
                    </p>
                </div>
                <div className={styles.jobAlert__ctaRow}>
                    <button type="submit" className='button primary-fill large'>
                        {loading ? 'Creating...' : 'Create Job Alert'}
                    </button>
                    <Link href="#" onClick={handleCloseClick} className='button transparent-fill large'>
                        Cancel
                    </Link>
                </div>
            </form>
        </div>
    );
};

export default JobAlertForm;
